import type { ILanguage } from "../../../interfaces";

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * ISO 3166-1 alpha-2 codes for flagcdn.com.
 * Keys are normalized language labels (see normalizeLanguageKey).
 */
const LANGUAGE_COUNTRY_CODES: Record<string, string> = {
  albanian: "al",
  american: "us",
  americanenglish: "us",
  amharic: "et",
  arabic: "sa",
  armenian: "am",
  azerbaijani: "az",
  basque: "es",
  belarusian: "by",
  bengali: "bd",
  bosnian: "ba",
  british: "gb",
  britishenglish: "gb",
  bulgarian: "bg",
  burmese: "mm",
  cantonese: "hk",
  catalan: "es",
  chinese: "cn",
  croatian: "hr",
  czech: "cz",
  danish: "dk",
  dutch: "nl",
  english: "us",
  estonian: "ee",
  farsi: "ir",
  filipino: "ph",
  finnish: "fi",
  french: "fr",
  georgian: "ge",
  german: "de",
  greek: "gr",
  gujarati: "in",
  hebrew: "il",
  hindi: "in",
  hungarian: "hu",
  icelandic: "is",
  indonesian: "id",
  irish: "ie",
  italian: "it",
  japanese: "jp",
  khmer: "kh",
  korean: "kr",
  latvian: "lv",
  lithuanian: "lt",
  macedonian: "mk",
  malay: "my",
  mandarin: "cn",
  marathi: "in",
  mongolian: "mn",
  norwegian: "no",
  persian: "ir",
  polish: "pl",
  portuguese: "pt",
  punjabi: "in",
  romanian: "ro",
  russian: "ru",
  serbian: "rs",
  simplifiedchinese: "cn",
  slovak: "sk",
  slovenian: "si",
  spanish: "es",
  swahili: "ke",
  swedish: "se",
  tagalog: "ph",
  tamil: "in",
  thai: "th",
  traditionalchinese: "tw",
  turkish: "tr",
  ukrainian: "ua",
  urdu: "pk",
  vietnamese: "vn",
  welsh: "gb",
};

const LANGUAGE_ALIASES: Record<string, string> = {
  "american english": "americanenglish",
  "british english": "britishenglish",
  "mandarin chinese": "mandarin",
  "simplified chinese": "simplifiedchinese",
  "traditional chinese": "traditionalchinese",
  "modern greek": "greek",
  "modern hebrew": "hebrew",
  "standard arabic": "arabic",
  "castilian spanish": "spanish",
};

function compactLanguageKey(value: string): string {
  return value.replace(/[\s-]+/g, "");
}

export function normalizeLanguageKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^\p{L}\p{N}\s-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function lookupCountryCode(key: string): string | null {
  if (!key) {
    return null;
  }

  const alias = LANGUAGE_ALIASES[key];
  if (alias && LANGUAGE_COUNTRY_CODES[alias]) {
    return LANGUAGE_COUNTRY_CODES[alias];
  }

  if (LANGUAGE_COUNTRY_CODES[key]) {
    return LANGUAGE_COUNTRY_CODES[key];
  }

  const compact = compactLanguageKey(key);
  if (LANGUAGE_COUNTRY_CODES[compact]) {
    return LANGUAGE_COUNTRY_CODES[compact];
  }

  return null;
}

export function findLanguageCatalogEntry(
  label: string,
  catalog: ILanguage[],
): ILanguage | undefined {
  const trimmed = label.trim();
  if (!trimmed) {
    return undefined;
  }

  if (GUID_REGEX.test(trimmed)) {
    return catalog.find((entry) => entry.id === trimmed);
  }

  const normalized = normalizeLanguageKey(trimmed);
  return catalog.find(
    (entry) => normalizeLanguageKey(entry.name) === normalized,
  );
}

function resolveFromTokens(normalized: string): string | null {
  const tokens = normalized.split(" ").filter((token) => token.length >= 3);

  for (const token of tokens) {
    const code = lookupCountryCode(token);
    if (code) {
      return code;
    }
  }

  return null;
}

function resolveFromPartialMatch(normalized: string): string | null {
  let best: { key: string; code: string } | null = null;

  for (const [key, code] of Object.entries(LANGUAGE_COUNTRY_CODES)) {
    if (key.length < 4) {
      continue;
    }

    if (
      normalized === key ||
      normalized.includes(key) ||
      key.includes(normalized)
    ) {
      if (!best || key.length > best.key.length) {
        best = { key, code };
      }
    }
  }

  return best?.code ?? null;
}

export function resolveLanguageCountryCode(
  label: string,
  catalog: ILanguage[],
): string | null {
  const normalized = normalizeLanguageKey(label);
  if (!normalized) {
    return null;
  }

  const direct = lookupCountryCode(normalized);
  if (direct) {
    return direct;
  }

  const catalogEntry = findLanguageCatalogEntry(label, catalog);
  if (catalogEntry) {
    const fromCatalog = lookupCountryCode(
      normalizeLanguageKey(catalogEntry.name),
    );
    if (fromCatalog) {
      return fromCatalog;
    }
  }

  const fromTokens = resolveFromTokens(normalized);
  if (fromTokens) {
    return fromTokens;
  }

  return resolveFromPartialMatch(normalized);
}

export function resolveLanguageInitials(
  label: string,
  catalog: ILanguage[],
): string {
  const catalogEntry = findLanguageCatalogEntry(label, catalog);
  const abbreviation = catalogEntry?.abbreviation?.trim();
  if (abbreviation && abbreviation.length >= 2) {
    return abbreviation.slice(0, 2).toUpperCase();
  }

  const normalized = normalizeLanguageKey(label);
  const words = normalized.split(" ").filter(Boolean);

  if (words.length >= 2) {
    return `${words[0][0] ?? ""}${words[1][0] ?? ""}`.toUpperCase();
  }

  if (words[0]) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return label.trim().slice(0, 2).toUpperCase() || "??";
}

export function buildLanguageFlagUrl(
  countryCode: string,
  size: 40 | 80 = 40,
): string {
  return `https://flagcdn.com/w${size}/${countryCode.toLowerCase()}.png`;
}
